import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { apiFetch } from '../../utils/apiClient';
import { CONFIG_URL } from '../../constants/apiEndpoints';
import logger from '../../utils/logger';

// Fetch supported file extensions from backend
// apiFetch already has 3 retries with exponential backoff (1s, 2s, 4s)
export const fetchFileExtensions = createAsyncThunk(
    'fileExtensions/fetchFileExtensions',
    async (_, { rejectWithValue }) => {
        try {
            const response = await apiFetch(CONFIG_URL, {
                method: 'GET'
            });
            logger.info('Fetched supported file extensions from API:', response);
            return response;
        } catch (error) {
            logger.error('Failed to fetch file extensions after retries:', error);
            return rejectWithValue({
                message: error?.detail?.message || error?.message || 'Failed to fetch supported file extensions'
            });
        }
    }
);

const fileExtensionsSlice = createSlice({
    name: 'fileExtensions',
    initialState: {
        extensions: [],
        dangerousExtensions: [],
        disallowedFilenameChars: [],
        uploadLimits: {
            maxFileSizeGB: null,
            maxTotalFileSizeGB: null,
            maxFilesPerRequest: null
        },
        loading: false,
        error: null,
        initialized: false
    },
    reducers: {
        clearFileExtensionsError: (state) => {
            state.error = null;
        }
    },
    extraReducers: (builder) => {
        builder
            .addCase(fetchFileExtensions.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(fetchFileExtensions.fulfilled, (state, action) => {
                state.loading = false;
                state.initialized = true;
                state.error = null;

                const payload = action.payload;

                // Handle extensions
                const rawExtensions = Array.isArray(payload)
                    ? payload
                    : (payload?.extensions || payload?.file_extensions || []);

                state.extensions = rawExtensions.map(ext =>
                    typeof ext === 'string' ? ext.toLowerCase().trim() : String(ext).toLowerCase().trim()
                );

                // Handle dangerous extensions
                if (payload?.dangerous_extensions) {
                    state.dangerousExtensions = payload.dangerous_extensions.map(ext =>
                        typeof ext === 'string' ? ext.toLowerCase().trim() : String(ext).toLowerCase().trim()
                    );
                }

                // Handle disallowed filename chars
                if (payload?.disallowed_filename_chars) {
                    state.disallowedFilenameChars = payload.disallowed_filename_chars;
                }
                // Handle upload limits
                if (payload?.upload_limits) {
                    state.uploadLimits = {
                        maxFileSizeGB: payload.upload_limits.max_file_size_gb ?? null,
                        maxTotalFileSizeGB: payload.upload_limits.max_total_file_size_gb ?? null,
                        maxFilesPerRequest: payload.upload_limits.max_files_per_request ?? null
                    };
                }
            })
            .addCase(fetchFileExtensions.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload?.message || 'Failed to fetch supported file extensions';
                state.initialized = false;
            });
    }
});

// Selectors
export const selectSupportedExtensions = (state) => state.fileExtensions.extensions;
export const selectDangerousExtensions = (state) => state.fileExtensions.dangerousExtensions;
export const selectDisallowedFilenameChars = (state) => state.fileExtensions.disallowedFilenameChars;
export const selectUploadLimits = (state) => state.fileExtensions.uploadLimits;
export const selectFileExtensionsInitialized = (state) => state.fileExtensions.initialized;
export const selectFileExtensionsLoading = (state) => state.fileExtensions.loading;
export const selectFileExtensionsError = (state) => state.fileExtensions.error;

// Returns true if extensions are loaded and available for upload
export const selectIsUploadEnabled = (state) => {
    return state.fileExtensions.initialized && 
           state.fileExtensions.extensions.length > 0 && 
           !state.fileExtensions.error;
};

export const { clearFileExtensionsError } = fileExtensionsSlice.actions;
export default fileExtensionsSlice.reducer;